import { css } from 'aphrodite';
import { FC, useMemo } from 'react';
import { ButtonBase } from '../../components/Button';
import { TagCheckIcon } from '../../components/svgs/icons/TagCheckIcon';
import { TagIcon } from '../../components/svgs/icons/TagIcon';
import { XIcon } from '../../components/svgs/icons/XIcon';
import { THEME, ThemeColors } from '../Theme/theme';
import { pillStyles, variantStyles } from './styles';

interface PillProps {
	size?: 'small' | 'medium';
	disabled?: boolean;
	label: string;
	/** Value passed to onClick/onRemove callbacks. Defaults to label if not provided. */
	value?: string;
	onRemove?: (value: string) => void;
	onClick?: (value: string) => void;
	/** Custom icon to display before the label */
	icon?: React.ReactNode;
}

export interface SolidColorPillProps extends PillProps {
	variant: 'keyword';
}

export interface MultiColorPillProps extends PillProps {
	variant: 'tag' | 'label' | 'category';
	color: keyof ThemeColors['pill'];
	selectable?: boolean;
	selected?: boolean;
}
const { colors } = THEME;
/**
 * - Any pill will become a clickable if an onClick or onRemove prop is provided
 * - In the case of multi-color pills, a color prop is required
 * - In the case of selectable pills, also provide an onClick handler and a selected state
 */
export const Pill: FC<React.PropsWithChildren<SolidColorPillProps | MultiColorPillProps>> = ({
	disabled,
	icon,
	label,
	value,
	onClick,
	onRemove,
	variant = 'tag',
	size = 'small',
	...rest
}) => {
	// Use value for callbacks if provided, otherwise fall back to label
	const callbackValue = value ?? label;

	const handleClick = () => {
		onClick?.(callbackValue);
	};
	const handleRemove = () => {
		onRemove?.(callbackValue);
	};

	const selectable = 'selectable' in rest ? rest?.selectable : false;
	const selected = 'selected' in rest ? rest?.selected : false;
	const clickable = !!onClick || !!onRemove;

	// So that we're not re-running multiple conditionals and reconstituing styles on every render
	const fontSize = useMemo(() => (size === 'small' ? pillStyles.smallFont : pillStyles.mediumFont), [size]);
	const color = useMemo(
		() =>
			'color' in rest && !!rest?.color && rest?.color?.toLowerCase() !== ('unknown' as unknown)
				? (rest.color.toLowerCase() as keyof ThemeColors['pill'])
				: 'blue',
		[rest]
	);
	const containerStyles = useMemo(
		() => (variant !== 'keyword' ? variantStyles[variant][color].container : variantStyles[variant].container),
		[variant, color]
	);
	const clickableStyles = useMemo(
		() => (variant !== 'keyword' ? variantStyles[variant][color].clickable : variantStyles[variant].clickable),
		[variant, color]
	);
	const iconStyles = useMemo(
		() => (variant !== 'keyword' ? variantStyles[variant][color].icon : variantStyles[variant].icon),
		[variant, color]
	);

	return (
		<div
			className={css(
				pillStyles.container,
				containerStyles,
				fontSize,
				clickable && clickableStyles,
				(selectable || variant === 'category' || !!icon) && pillStyles.leftPaddingOverride,
				!!onRemove && pillStyles.rightPaddingOverride
			)}
		>
			{icon && <span className={css(pillStyles.icon, iconStyles)}>{icon}</span>}
			{variant === 'category' && !icon && (
				<TagIcon className={css(pillStyles.tagIcon, iconStyles)} fillColor='currentColor' />
			)}
			{onClick || selectable ? (
				<ButtonBase
					aria-label={`Select ${label}`}
					aria-pressed={selectable ? selected : undefined}
					className={css(pillStyles.labelButton)}
					disabled={disabled}
					onClick={handleClick}
				>
					{variant !== 'category' && selectable && (
						<TagCheckIcon
							role='checkbox'
							aria-label={`${label} checkbox`}
							height={20}
							width={20}
							checkFillColor={selected ? colors.icon.primary : colors.icon.disabled}
							aria-checked={selected}
						/>
					)}
					<span className={css(pillStyles.label)}>{rest.children || label}</span>
				</ButtonBase>
			) : (
				<span className={css(pillStyles.label)}>{rest.children || label}</span>
			)}
			{!!onRemove && (
				<ButtonBase aria-label={`Remove ${label}`} disabled={disabled} onClick={handleRemove}>
					<span className={css(pillStyles.closeIcon, iconStyles)}>
						<XIcon role='img' aria-label='Remove item icon' height={12} width={12} />
					</span>
				</ButtonBase>
			)}
		</div>
	);
};
